import React, { useState } from "react";
import { Project } from "@/types";
import { CardHeader, CardContent } from "@/components/ui/card";
import { MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { formatNumber } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import getProjectTranslation from "@/utils/getProjectTranslation";

interface ProjectInfoProps {
  project: Project;
  displayInSheet?: boolean;
}

const ProjectInfo: React.FC<ProjectInfoProps> = ({
  project,
  displayInSheet,
}) => {
  const { t, language } = useLanguage();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  return (
    <div className="flex-1">
      <CardHeader className={`p-4 pb-2 ${displayInSheet ? "px-0" : "px-4"}`}>
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold">
            {getProjectTranslation(project.nameTranslations, language) || project.title}
          </h3>
          <div className="flex items-center text-sm text-muted-foreground mt-1">
            <MapPin className="h-3.5 w-3.5 mr-1" />
            <span>{project.location}</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className={`p-4 pt-0 ${displayInSheet ? "px-0" : "px-4"}`}>
        {project.description && (
          <div className="mb-4">
            <p
              className={`text-sm text-gray-600 ${
                !isDescriptionExpanded && displayInSheet ? "line-clamp-3" : ""
              }`}
            >
              {project.description}
            </p>
            {displayInSheet && project.description.length > 150 && (
              <Button
                variant="link"
                className="p-0 h-auto text-sm text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              >
                {isDescriptionExpanded ? (
                  <>
                    {t("common.showLess")}
                    <ChevronUp className="h-5 w-5" />
                  </>
                ) : (
                  <>
                    {t("common.showMore")}
                    <ChevronDown className="h-5 w-5" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-8 mb-2">
          {project.area && (
            <div>
              <p className="text-xs text-muted-foreground">
                {t("projectDetail.area")}
              </p>
              <p className="text-sm font-bold">
                {formatNumber(project.area / 10000, language)} ha
              </p>
            </div>
          )}

          {project.forecastedAmountYearly && (
            <div>
              <p className="text-xs text-muted-foreground">
                {t("projectDetail.carbonStoredPerYear")}
              </p>
              <p className="text-sm font-bold">
                {formatNumber(project.forecastedAmountYearly / 1000, language)}{" "}
                t/year
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default ProjectInfo;
